/**
 * Session statistics hook
 *
 * Tracks user voting statistics in localStorage.
 */

import { useState, useCallback, useEffect } from 'react';
import type { SessionStats, AiStatus } from '@doyoulikethis/shared';

const STORAGE_KEY = 'doyoulikethis_session_stats';

const defaultStats: SessionStats = {
  votes_this_session: 0,
  liked: 0,
  disliked: 0,
  skipped: 0,
  correct_ai_identifications: 0,
  incorrect_ai_identifications: 0,
  correct_human_identifications: 0,
  incorrect_human_identifications: 0,
  by_category: {},
  voted_image_ids: [],
};

function loadStats(): SessionStats {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      return { ...defaultStats, ...JSON.parse(stored) };
    }
  } catch (error) {
    console.error('Failed to load session stats:', error);
  }
  return { ...defaultStats };
}

function saveStats(stats: SessionStats): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(stats));
  } catch (error) {
    console.error('Failed to save session stats:', error);
  }
}

export function useSessionStats() {
  const [stats, setStats] = useState<SessionStats>(loadStats);

  // Save stats whenever they change
  useEffect(() => {
    saveStats(stats);
  }, [stats]);

  /**
   * Record a vote
   */
  const recordVote = useCallback(
    (params: {
      imageId: string;
      liked: boolean;
      reasons: string[];
      aiStatus: AiStatus;
      categories: string[];
    }) => {
      setStats((prev) => {
        const newStats = { ...prev };

        // Update basic counts
        newStats.votes_this_session += 1;
        if (params.liked) {
          newStats.liked += 1;
        } else {
          newStats.disliked += 1;
        }

        // Track voted image
        if (!newStats.voted_image_ids.includes(params.imageId)) {
          newStats.voted_image_ids = [...newStats.voted_image_ids, params.imageId];
        }

        // Track AI perception accuracy
        const saidAiSlop = params.reasons.includes('looks_ai_slop');
        const saidHumanSoul = params.reasons.includes('looks_human_soul');

        if (params.aiStatus === 'full') {
          // Image is AI-generated
          if (saidAiSlop) {
            newStats.correct_ai_identifications += 1;
          } else if (saidHumanSoul) {
            newStats.incorrect_human_identifications += 1; // Thought AI was human
          }
        } else if (params.aiStatus === 'none') {
          // Image is human-made
          if (saidHumanSoul) {
            newStats.correct_human_identifications += 1;
          } else if (saidAiSlop) {
            newStats.incorrect_ai_identifications += 1; // Thought human was AI
          }
        }

        // Track by category
        const newByCategory = { ...newStats.by_category };
        for (const category of params.categories) {
          if (!newByCategory[category]) {
            newByCategory[category] = { liked: 0, disliked: 0 };
          }
          if (params.liked) {
            newByCategory[category] = {
              ...newByCategory[category],
              liked: newByCategory[category].liked + 1,
            };
          } else {
            newByCategory[category] = {
              ...newByCategory[category],
              disliked: newByCategory[category].disliked + 1,
            };
          }
        }
        newStats.by_category = newByCategory;

        return newStats;
      });
    },
    []
  );

  /**
   * Record a skip
   */
  const recordSkip = useCallback((imageId: string) => {
    setStats((prev) => {
      const newStats = { ...prev };
      newStats.skipped += 1;

      // Track skipped image so we don't show it again
      if (!newStats.voted_image_ids.includes(imageId)) {
        newStats.voted_image_ids = [...newStats.voted_image_ids, imageId];
      }

      return newStats;
    });
  }, []);

  /**
   * Reset all stats
   */
  const resetStats = useCallback(() => {
    setStats({ ...defaultStats });
  }, []);

  /**
   * Export stats as JSON
   */
  const exportStats = useCallback(() => {
    return JSON.stringify(stats, null, 2);
  }, [stats]);

  /**
   * Get AI detection accuracy percentage
   */
  const getAccuracy = useCallback(() => {
    const total =
      stats.correct_ai_identifications +
      stats.incorrect_ai_identifications +
      stats.correct_human_identifications +
      stats.incorrect_human_identifications;

    if (total === 0) return null;

    const correct =
      stats.correct_ai_identifications + stats.correct_human_identifications;

    return Math.round((correct / total) * 100);
  }, [stats]);

  /**
   * Get skip rate percentage
   */
  const getSkipRate = useCallback(() => {
    const total = stats.votes_this_session + stats.skipped;
    if (total === 0) return 0;
    return Math.round((stats.skipped / total) * 100);
  }, [stats]);

  return {
    stats,
    votedImageIds: stats.voted_image_ids,
    recordVote,
    recordSkip,
    resetStats,
    exportStats,
    getAccuracy,
    getSkipRate,
  };
}
